import { useEffect, useRef } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import {
    Bath,
    Building2,
    ChefHat,
    DoorOpen,
    Droplets,
    Hammer,
    Layers,
    PaintRoller,
    PanelTop,
    PencilRuler,
    ShieldCheck,
    Sparkles,
    Umbrella,
    Wind,
    Zap,
} from "lucide-react";

gsap.registerPlugin(ScrollTrigger);

const SERVICES = [
    { name: "Interior Design & Space Planning", blurb: "Layouts that flow beautifully", Icon: PencilRuler },
    { name: "Carpentry & Custom Built-ins", blurb: "Made-to-measure woodwork", Icon: Hammer },
    { name: "Painting & Wall Finishes", blurb: "Flawless colour and texture", Icon: PaintRoller },
    { name: "Flooring Works", blurb: "Wood, tile, vinyl and more", Icon: Layers },
    { name: "Electrical Services", blurb: "Safe, certified wiring", Icon: Zap },
    { name: "Plumbing Services", blurb: "Leak-free, done right", Icon: Droplets },
    { name: "Kitchen Renovation", blurb: "The heart of your home", Icon: ChefHat },
    { name: "Bathroom Renovation", blurb: "Spa-grade makeovers", Icon: Bath },
    { name: "Ceiling & Partition Works", blurb: "Structure and separation", Icon: PanelTop },
    { name: "Doors & Windows", blurb: "Secure, stylish fittings", Icon: DoorOpen },
    { name: "Air Conditioning & Ventilation", blurb: "Quiet climate comfort", Icon: Wind },
    { name: "Roofing & Waterproofing", blurb: "Sealed against the elements", Icon: Umbrella },
    { name: "Home Automation & Security", blurb: "Smart, protected living", Icon: ShieldCheck },
    { name: "Commercial Renovation", blurb: "Workspaces that work", Icon: Building2 },
    { name: "Post-Renovation Cleaning", blurb: "Handover-ready shine", Icon: Sparkles },
];

export const Services = () => {
    const sectionRef = useRef(null);
    const headingRef = useRef(null);
    const supportRef = useRef(null);
    const gridRef = useRef(null);

    useEffect(() => {
        if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return undefined;

        const ctx = gsap.context(() => {
            const heading = headingRef.current;
            const support = supportRef.current;
            const cards = gridRef.current?.querySelectorAll("[data-service-card]");
            if (!heading || !support || !cards?.length) return;

            const isMobile = window.matchMedia("(max-width: 767px)").matches;

            gsap.set(heading, { opacity: 0, y: 18 });
            gsap.set(support, { opacity: 0, y: 16 });
            gsap.set(cards, { opacity: 0, y: isMobile ? 16 : 24, scale: 0.985 });

            gsap
                .timeline({
                    delay: 0.45,
                    scrollTrigger: {
                        trigger: sectionRef.current,
                        start: "top 78%",
                        toggleActions: "play none none none",
                    },
                })
                .to(heading, {
                    opacity: 1,
                    y: 0,
                    duration: 0.62,
                    ease: "power3.out",
                })
                .to(
                    support,
                    {
                        opacity: 1,
                        y: 0,
                        duration: 0.58,
                        ease: "power3.out",
                    },
                    "-=0.38",
                )
                .to(
                    cards,
                    {
                        opacity: 1,
                        y: 0,
                        scale: 1,
                        duration: 0.62,
                        stagger: isMobile ? 0.05 : 0.08,
                        ease: "power3.out",
                    },
                    "-=0.28",
                );
        }, sectionRef);

        return () => ctx.revert();
    }, []);

    return (
        <section
            id="services"
            ref={sectionRef}
            data-testid="services-section"
            className="relative bg-white py-24 sm:py-32"
        >
            <div className="mx-auto max-w-7xl px-5 sm:px-8">
                <div className="mb-14 flex flex-col gap-8 lg:flex-row lg:items-end lg:justify-between">
                    <div ref={headingRef}>
                        <p className="mb-4 font-mono text-xs font-semibold uppercase tracking-[0.24em] text-brand-gold">
                            01 — Services
                        </p>
                        <h2 className="max-w-xl font-display text-4xl font-semibold leading-[1.02] tracking-tighter text-brand-deep sm:text-5xl">
                            Everything You Need to <span className="italic text-brand-teal">Renovate</span>
                        </h2>
                    </div>
                    <p
                        ref={supportRef}
                        className="max-w-sm text-sm leading-relaxed text-brand-muted sm:text-base"
                    >
                        Fifteen crafts, one platform. Every trade your renovation needs — coordinated, scheduled and
                        delivered under a single wish.
                    </p>
                </div>

                <div
                    ref={gridRef}
                    className="grid grid-cols-2 gap-px overflow-hidden rounded-[28px] border border-brand-line bg-brand-line sm:grid-cols-3 lg:grid-cols-5"
                >
                    {SERVICES.map((service, i) => (
                        <div
                            key={service.name}
                            data-service-card
                            data-testid={`service-card-${i + 1}`}
                            className="group relative bg-white p-5 transition-[transform,box-shadow,background-color] duration-300 hover:z-10 hover:-translate-y-1 hover:bg-brand-surface hover:shadow-[0_16px_40px_rgba(2,27,30,0.08)] sm:p-6"
                        >
                            <div className="mb-5 flex h-11 w-11 items-center justify-center rounded-2xl bg-brand-mist text-brand-teal transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:bg-brand-gold/25 group-hover:text-brand-deep">
                                <service.Icon className="h-5 w-5" strokeWidth={1.8} />
                            </div>
                            <h3 className="text-sm font-semibold leading-snug text-brand-deep">{service.name}</h3>
                            <p className="mt-1.5 hidden text-xs leading-relaxed text-brand-muted sm:block">{service.blurb}</p>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
};
